import { formatDateString } from "@/v2-common/utils/utils.date"

export type LicenseSeatPerson = {
  UserId: number
  FirstName?: string | null
  LastName?: string | null
  Email?: string | null
}

/**
 * What the operator is saving when limiting a license to named people: the
 * people, and the seats the customer pays for. `seats` is `null` while the
 * count follows the people picked (the default: one seat per person), `""`
 * once the operator has cleared it (no count), or the digits typed.
 */
export type LicenseSeatLimitDraft = {
  userIds: number[]
  seats: string | null
}

export const EMPTY_SEAT_LIMIT_DRAFT: LicenseSeatLimitDraft = {
  userIds: [],
  seats: null,
}

/**
 * The seat count a draft saves: the people picked while it follows them, no
 * count when cleared, else the number typed. `followsPicked` false (a
 * billing-derived license, where the count is the subscription's and a typed
 * number is an override) never derives one from the people.
 */
export function seatLimitCountOf(
  draft: LicenseSeatLimitDraft,
  followsPicked: boolean,
): number | null {
  if (draft.seats === null)
    return followsPicked && draft.userIds.length > 0
      ? draft.userIds.length
      : null
  if (draft.seats === "") return null
  return Number(draft.seats)
}

/** A typed count is a whole number of at least one; following or cleared is always fine. */
export function isSeatLimitCountValid(draft: LicenseSeatLimitDraft): boolean {
  if (draft.seats === null || draft.seats === "") return true
  const count = Number(draft.seats)
  return Number.isInteger(count) && count >= 1
}

/** How a person on a seat list is named: full name, else e-mail, else id. */
export function seatPersonNameOf(person: LicenseSeatPerson): string {
  const name = [person.FirstName, person.LastName].filter(Boolean).join(" ")
  return name || person.Email || `User ${person.UserId}`
}

/** One seat row of the customer-wide holders read. */
export type LicenseSeatHolder = LicenseSeatPerson & {
  LicenseId: number
  ProductName: string
}

/** The holders of each license, in the read's order (by name). */
export function groupSeatHoldersByLicense<Holder extends LicenseSeatHolder>(
  holders: readonly Holder[],
): Map<number, Holder[]> {
  const byLicense = new Map<number, Holder[]>()
  for (const holder of holders) {
    const group = byLicense.get(holder.LicenseId) ?? []
    group.push(holder)
    byLicense.set(holder.LicenseId, group)
  }
  return byLicense
}

/** The seats each person holds, as the licenses' product names. */
export function groupSeatProductsByUser(
  holders: readonly LicenseSeatHolder[],
): Map<number, string[]> {
  const byUser = new Map<number, string[]>()
  for (const holder of holders) {
    const products = byUser.get(holder.UserId) ?? []
    if (!products.includes(holder.ProductName))
      products.push(holder.ProductName)
    byUser.set(holder.UserId, products)
  }
  return byUser
}

/**
 * Where one person stands on one of their customer's seated licenses (the
 * MCP servers): the license is limited to named people and they are one
 * ("held") or are not ("missing"), or it is not limited and reaches every
 * active user of the customer ("everyone").
 */
export type UserLicenseSeatStanding = "held" | "missing" | "everyone"

export type UserLicenseSeat = {
  LicenseId: number
  ProductName: string
  PlanName: string | null
  standing: UserLicenseSeatStanding
  /** When the seat was granted and by whom (null = mechanically); a held seat only. */
  GrantedAt: string | null
  GrantedByUserName: string | null
}

/** The word after the product name: "Placepoint Dataset MCP · seat held". */
export const USER_LICENSE_SEAT_STANDING_LABEL: Record<
  UserLicenseSeatStanding,
  string
> = {
  held: "seat held",
  missing: "no seat",
  everyone: "everyone",
}

/** Held seats first, then the limited licenses the person is not named on, then what reaches everyone. */
const STANDING_ORDER: Record<UserLicenseSeatStanding, number> = {
  held: 0,
  missing: 1,
  everyone: 2,
}

type SeatedLicenseLike = {
  Id: number
  ProductName: string
  PlanName?: string | null
  SeatsAreAssigned: boolean
  SeatsRestricted: boolean
}

type SeatHolderLike = Pick<LicenseSeatHolder, "LicenseId" | "UserId"> & {
  GrantedAt?: string | null
  GrantedByUserName?: string | null
}

/**
 * One person's standing on each seated license among `licenses` (the
 * customer's live licenses; the ones whose product does not assign seats
 * are skipped), read against the customer's seat holders.
 */
export function userLicenseSeatsOf(
  licenses: readonly SeatedLicenseLike[],
  holders: readonly SeatHolderLike[],
  userId: number,
): UserLicenseSeat[] {
  return licenses
    .filter((license) => license.SeatsAreAssigned)
    .map((license): UserLicenseSeat => {
      const seat = holders.find(
        (holder) => holder.LicenseId === license.Id && holder.UserId === userId,
      )
      return {
        LicenseId: license.Id,
        ProductName: license.ProductName,
        PlanName: license.PlanName ?? null,
        standing: !license.SeatsRestricted
          ? "everyone"
          : seat
            ? "held"
            : "missing",
        GrantedAt: seat?.GrantedAt ?? null,
        GrantedByUserName: seat?.GrantedByUserName ?? null,
      }
    })
    .toSorted(
      (left, right) =>
        STANDING_ORDER[left.standing] - STANDING_ORDER[right.standing],
    )
}

/** "Placepoint Dataset MCP: seat held, Placepoint MCP (Norway): everyone", for copy and export. */
export function formatUserLicenseSeats(seats: readonly UserLicenseSeat[]) {
  return seats
    .map(
      (seat) =>
        `${seat.ProductName}: ${USER_LICENSE_SEAT_STANDING_LABEL[seat.standing]}`,
    )
    .join(", ")
}

/** Where a seat is changed, named the same way under every standing. */
const SEAT_CHANGE_PLACE =
  "the customer panel's Licenses & add-ons tab (row menu, Seats)"

/**
 * One person's standing on one seated license, in full, for the hover on
 * its pill: what the standing means for them and where it is changed.
 */
export function describeUserLicenseSeat(
  seat: UserLicenseSeat,
  userName: string,
  customerName: string,
) {
  const license = `${customerName}'s ${seat.ProductName} license${
    seat.PlanName && seat.PlanName !== seat.ProductName
      ? ` (${seat.PlanName})`
      : ""
  }`
  switch (seat.standing) {
    case "held": {
      const granted = seat.GrantedAt
        ? ` Granted ${formatDateString(seat.GrantedAt)}${
            seat.GrantedByUserName ? ` by ${seat.GrantedByUserName}` : ""
          }.`
        : ""
      return `${userName} holds a seat on ${license}. The license is limited to named people, and a seat is what lets them use it.${granted} Revoke it on ${SEAT_CHANGE_PLACE}.`
    }
    case "missing":
      return `No seat: ${license} is limited to named people and ${userName} is not one of them, so it does not reach them. Name them on ${SEAT_CHANGE_PLACE}.`
    case "everyone":
      return `Not limited: every active user of ${customerName} has what ${license} carries, ${userName} included. It can be limited to named people on ${SEAT_CHANGE_PLACE}.`
  }
}
